import {
  MarkdownBaseParser,
  Types,
  toGeolocation,
} from "@citychallenge/common";
import { observable, computed } from "mobx";
import { convertMetersToKm } from "@citychallenge/common";

import {
  GameEntity,
  GameEntityGameTypeEnum,
  GamePlayerDto,
  GeoPointDto,
  ThemeEntity,
  MediaFileDto,
  MediaColorDto,
} from "@citychallenge/api-public";
import { Translation, TransTokens } from "../i18n";
import { Preference } from "../services";
import { GameReleaseModel } from "./game-release.model";
import { GameSessionModel } from "../player/game-session/game-session.model";
import dayjs from "dayjs";

const tokens = TransTokens.create({
  numChallenges: "{{numChallenges}} challenges",
  trivia: "Trivia",
  story: "Story",
  minigame: "Minigames",
});

/**
 * A story loaded from the share-link endpoint rather than the division
 * catalogue. `/client/division/{id}/games/{gameId}` serves GamePlayerDto — a
 * deliberate subset — so everything the catalogue carries (theme, gameType,
 * reviewCount, pricing, map coordinates, …) is absent at runtime.
 *
 * Exposing that load as `GameModel` would type those fields as present, which
 * is how a caller ends up reading `undefined` with no compile error. Consumers
 * of a single story take this view instead; reach for `GameModel` only where
 * the value provably came from the catalogue.
 */
export type PlayerGameModel = Pick<
  GameModel,
  | "entityType"
  | "id"
  | "name"
  | "description"
  | "preview"
  | "image"
  | "storyIcon"
  | "published"
  | "updated"
  | "hideLeaderboard"
  | "distanceInMeters"
  | "distanceText"
  | "activeSession"
  | "state"
  | "isUnlocked"
  | "setHasOpenend"
> & {
  release: PlayerGameReleaseModel;
};

/**
 * The release a share-link story carries. `GameReleaseModel` is built by
 * assigning the payload over the class, so the members GamePlayerDto leaves out
 * are `undefined` at runtime however the class declares them — the capability
 * flags (`containsAr`, `containsVr`, `containsMagneto`, `containsCamera`,
 * `supportsDynamicDownloads`) read as required but never arrive.
 *
 * `containsGps` is here because the player gates on it (see the environment
 * guard); the rest of what the engine needs is the language list, the version
 * and the game the bundle is downloaded under.
 */
export type PlayerGameReleaseModel = Pick<
  GameReleaseModel,
  "id" | "releaseVersion" | "languages" | "containsGps" | "game"
>;

export class GameModel implements GameEntity {
  public entityType = "GameModel";

  @observable
  public id!: string;

  @observable
  public name!: string;

  @observable
  public coordinates?: GeoPointDto;

  @observable
  public image?: string;

  @observable
  public description?: string;

  @computed
  public get preview() {
    return MarkdownBaseParser.parsePreview(this.description);
  }

  @observable
  public hideLeaderboard?: boolean;

  @observable
  public gameType!: GameEntityGameTypeEnum;

  @observable
  public created?: string;

  @observable
  public updated?: string;

  @observable
  public release!: GameReleaseModel;

  @observable
  public published?: boolean;

  @observable
  public distanceInMeters?: number;

  @observable
  public timeInMinutes?: number;

  @observable
  public numChallenges?: number;

  @observable
  public hideUntil?: string;

  @observable
  public mainMedia?: MediaFileDto | MediaColorDto;

  @observable
  public customMainMedia?: MediaFileDto | MediaColorDto;

  /**
   * Resolves the thumbnail to display: the builder's custom override when set,
   * otherwise the auto-selected media from the story's first block.
   */
  @computed
  public get displayMainMedia(): MediaFileDto | MediaColorDto | undefined {
    return this.customMainMedia ?? this.mainMedia;
  }

  @computed
  public get isVisible() {
    // Always show if user has a session
    if (this.activeSession) return true;

    // Legacy hidden switch
    if (this.hidden) return false;

    // Hide game from overview if it has a hide switch
    // and time is has not expired yet
    if (this.hideUntil && dayjs(this.hideUntil) > dayjs()) return false;

    return true;
  }

  @observable
  public reviewCount?: number;

  @observable
  public reviewRating?: number;

  @observable
  public theme?: ThemeEntity;

  @observable
  public hidden?: boolean;

  @observable
  public footerText?: string;

  @observable
  public enableIconOverlay?: boolean;

  @observable
  public storyIcon?: string;

  @computed
  public get state() {
    if (this.activeSession?.completionPercentage === 100) return "completed";
    if (this.activeSession) return "started";
    return "default";
  }

  @computed
  public get isUnlocked() {
    return this._hasOpened.value || this.state !== "default";
  }

  public setHasOpenend(value: boolean) {
    this._hasOpened.setValue(value);
  }

  public baseScenario: undefined;
  public releases: undefined;

  /**
   * Builds the model from a share-link payload. GamePlayerDto types every
   * field optional and omits the catalogue columns entirely, so it cannot
   * satisfy GameEntity — the assertion below is the one place that gap is
   * bridged, and the guard ahead of it checks the three members this class
   * treats as always-present. Callers get the narrowed `PlayerGameModel` view,
   * which is what stops the missing fields from being read downstream.
   */
  public static fromPlayerPayload(
    payload: GamePlayerDto,
    hasOpened: Preference<boolean>
  ): GameModel {
    if (!payload.id || !payload.name || !payload.release) {
      throw new Error(
        "Player payload is missing id, name or release — cannot build a story"
      );
    }

    return new GameModel(payload as GameEntity, hasOpened);
  }

  constructor(entity: GameEntity, private _hasOpened: Preference<boolean>) {
    Object.assign(this, entity);

    if (entity.release) {
      this.release = new GameReleaseModel(entity.release, this);
    }
  }

  @computed
  public get startingLocation(): Types["GeoLocation"] | undefined {
    if (!this.coordinates) return undefined;

    return toGeolocation(this.coordinates.coordinates as [number, number]);
  }

  @computed
  public get ratingText(): string | Translation | undefined {
    return this.reviewRating
      ? `${this.reviewRating.toPrecision(2)}`
      : undefined;
  }

  @computed
  public get durationText(): string | undefined {
    return this.timeInMinutes ? `${this.timeInMinutes} min` : undefined;
  }

  @computed
  public get distanceText(): string | undefined {
    return this.distanceInMeters
      ? convertMetersToKm(this.distanceInMeters)
      : undefined;
  }

  @computed
  public get themeText(): string | undefined {
    return this.theme ? this.theme.name : undefined;
  }

  @computed
  public get gameTypeText(): Translation | undefined {
    return (tokens as { [key: string]: Translation })[this.gameType || ""];
  }

  @computed
  public get challengesText(): string | Translation | undefined {
    return this.numChallenges ? tokens.numChallenges : undefined;
  }

  @observable
  public activeSession?: GameSessionModel = undefined;
}
